/**
 * usePasskey — passkey enrollment for an already-signed-in user.
 *
 * Login is passwordless and lives in AuthContext.loginWithPasskey (it owns the
 * session). This hook only covers enrolling a new passkey from the account page.
 *
 * Browser support is feature-detected; call sites should hide their entry
 * points when `isSupported` is false.
 */

import { useCallback, useState } from 'react';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { passkeyService } from '@/api/services';
import { getPasskeyErrorMessage } from '@/lib/errors/passkey-error';

export interface UsePasskeyResult {
  isSupported: boolean;
  isBusy: boolean;
  enrollPasskey: (deviceName: string) => Promise<{ credentialId: string; deviceName: string }>;
}

export function usePasskey(): UsePasskeyResult {
  const [isBusy, setIsBusy] = useState(false);

  const enrollPasskey = useCallback(async (deviceName: string) => {
    setIsBusy(true);
    try {
      const { options, challengeId } = await passkeyService.registerStart();
      const credential = await startRegistration({ optionsJSON: options });
      const result = await passkeyService.registerFinish({ challengeId, credential, deviceName });
      return { credentialId: result.credentialId, deviceName: result.deviceName };
    } catch (error) {
      throw Object.assign(new Error(getPasskeyErrorMessage(error)), { cause: error });
    } finally {
      setIsBusy(false);
    }
  }, []);

  return {
    isSupported: browserSupportsWebAuthn(),
    isBusy,
    enrollPasskey,
  };
}

export default usePasskey;
